import React, { useState, useEffect } from 'react';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useTutorialContext } from '../../contexts/TutorialContext';
import { UserPreferences } from '../../types';
import Modal from '../ui/Modal';

interface UserPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UserPreferencesModal: React.FC<UserPreferencesModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    preferences,
    updatePreferences,
    loading,
    serviceConfigLoading: configLoading,
    serviceConfigError: configError,
    getAvailableServices,
  } = usePreferences();

  const { resetAllTutorials } = useTutorialContext();

  const [localPreferences, setLocalPreferences] = useState<
    Partial<UserPreferences>
  >({});
  const [isSaving, setIsSaving] = useState(false);

  // Reset local state whenever modal opens to ensure fresh data
  useEffect(() => {
    if (isOpen && preferences) {
      const prefs: Partial<UserPreferences> = {};

      if (process.env.REACT_APP_TRANSCRIBING_SERVICE_SELECTION === 'true') {
        prefs.transcription_service = preferences.transcription_service || '';
      }

      if (process.env.REACT_APP_UTTERANCE_META_COLUMN_SELECTION === 'true') {
        prefs.transcript = {
          utterance_meta_column:
            preferences.transcript?.utterance_meta_column ?? false,
        };
      }

      setLocalPreferences(prefs);
    }
  }, [isOpen, preferences]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updatePreferences(localPreferences);
      onClose();
    } catch (error) {
      console.error('Failed to update preferences:', error);
      // You could add toast notification here
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Get available services for dropdown
  const availableServices = getAvailableServices();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="User Preferences"
      maxWidth="md"
    >
      <div className="space-y-6">
        {/* Transcription Service Selection */}
        {process.env.REACT_APP_TRANSCRIBING_SERVICE_SELECTION === 'true' && (
          <div>
            <label
              htmlFor="transcription-service"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3"
            >
              Transcription Service
            </label>

            {configError ? (
              <div className="text-red-600 dark:text-red-400 text-sm mb-2">
                {configError}
              </div>
            ) : configLoading ? (
              <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">
                Loading available services...
              </div>
            ) : availableServices.length > 0 ? (
              <>
                <select
                  id="transcription-service"
                  value={localPreferences.transcription_service || ''}
                  onChange={(e) =>
                    setLocalPreferences((prev) => ({
                      ...prev,
                      transcription_service: e.target.value,
                    }))
                  }
                  disabled={configLoading}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {availableServices.map((service) => (
                    <option key={service.key} value={service.key}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <div className="text-gray-500 dark:text-gray-400 text-sm">
                No transcription services available
              </div>
            )}
          </div>
        )}

        {/* Utterance Meta Column Selection */}
        {process.env.REACT_APP_UTTERANCE_META_COLUMN_SELECTION === 'true' && (
          <div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={
                  localPreferences.transcript?.utterance_meta_column ?? false
                }
                onChange={(e) =>
                  setLocalPreferences((prev) => ({
                    ...prev,
                    transcript: {
                      ...prev.transcript,
                      utterance_meta_column: e.target.checked,
                    },
                  }))
                }
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                Use column layout for utterance metadata
              </span>
            </label>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 ml-7">
              Display speaker and timestamp information in a separate column
              instead of a header panel
            </p>
          </div>
        )}

        {/* Tutorial Reset */}
        {process.env.REACT_APP_DEBUG_TUTORIALS_RESET === 'true' && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Tutorial Settings
            </h3>
            <button
              type="button"
              onClick={() => {
                resetAllTutorials();
                // Optionally show a confirmation message
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Reset All Tutorials
            </button>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              This will show all tutorials again as if you're a new user
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
